import { setPaymentToken } from "../redux/slices/paymentSlice";
import { getPaymentLinkHeaders } from "./paymentLinkHeaders";

const BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Thrown when a payment-link endpoint keeps rejecting the request as
 * unauthorized even after trying to obtain a fresh token — i.e. the
 * payment link itself is no longer usable, or the backend is otherwise
 * refusing to issue a token for this reff_no.
 */
export class SessionExpiredError extends Error {
  constructor(message = "Sesi Anda telah kadaluarsa. Silakan refresh halaman.") {
    super(message);
    this.name = "SessionExpiredError";
  }
}

/**
 * Re-mint a payment-link-scoped token from the correct public endpoint.
 *
 * There are two distinct reff_no's in this domain and each needs a different
 * endpoint — mixing them up silently 404s and looks like an expired session:
 * - kind "link": the parent PaymentLink's own reff_no (what FirstForm/SecondForm
 *   have, before any payment attempt exists) -> POST /api/payment-link
 * - kind "history": a specific payment attempt's own reff_no (PaymentLinkHistory.reff_no,
 *   what ThirdForm/OvoWaitingPage/PaymentInstructionCard have, once proceed-inquiry
 *   has run) -> POST /api/payment-link/get-by-reff
 *
 * Both are public/token-free endpoints that issue a fresh token in the response.
 */
async function refreshToken({ kind, reffNo }) {
  if (!reffNo) return null;

  const url = kind === "history"
    ? `${BASE_URL}/api/payment-link/get-by-reff`
    : `${BASE_URL}/api/payment-link`;

  const response = await fetch(url, {
    method: "POST",
    headers: getPaymentLinkHeaders(),
    body: JSON.stringify({ reff_no: reffNo }),
  });

  if (!response.ok) return null;
  const result = await response.json();
  return result?.token || null;
}

/**
 * Fetch an authenticated payment-link endpoint using the in-memory bearer
 * token. If the backend responds 401 (missing/invalid/expired token),
 * transparently re-mints a token via `refresh` and retries once.
 *
 * @param {string} url
 * @param {Object} params
 * @param {Function} params.dispatch - redux dispatch, used to persist the refreshed token
 * @param {string|null} params.token - current bearer token, if any
 * @param {{kind: "link"|"history", reffNo: string}} params.refresh - how to re-mint a token on 401
 * @param {string} [params.method]
 * @param {string} [params.body]
 * @returns {Promise<Response>}
 */
export async function authorizedFetch(url, { dispatch, token, refresh, ...options } = {}) {
  const doFetch = (bearerToken) =>
    fetch(url, { ...options, headers: getPaymentLinkHeaders(bearerToken) });

  let response = await doFetch(token);

  if (response.status === 401) {
    const freshToken = await refreshToken(refresh || {});

    if (!freshToken) {
      throw new SessionExpiredError();
    }

    dispatch(setPaymentToken(freshToken));
    response = await doFetch(freshToken);

    if (response.status === 401) {
      throw new SessionExpiredError();
    }
  }

  return response;
}
